import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import {grey, indigo} from '@mui/material/colors'
import {DataGrid, GridColDef, GridColumnHeaderParams, GridValueGetterParams} from '@mui/x-data-grid'
import {ProjectRepo} from 'app/hooks/api_repos'
import dayjs from 'dayjs'
import {ISavedSearchFE} from 'interfaces/ISavedSearchFE'
import * as React from 'react'
import {useSelector} from 'react-redux'
import {selectSavedSearch} from 'redux/selectors/savedSearchSelector'
import toastr from 'toastr'
import ShowMoreText from '../ShowMoreText'
import ConceptsList from '../concepts/ConceptsList'
import EmptyStateCard from '../emptyStates/emptyFolder'
import CreateProjectDialog from './CreateProjectDialog'

interface IProject {
  id: string
  name: string
  createdAt: string
  description: string
}

export default function SaveSearchToProjectDialog({
  discoveryRequest,
  show,
  setShow,
}: {
  discoveryRequest: ISavedSearchFE
  show: boolean
  setShow: (t: boolean) => void
}) {
  const [openCreateProject, setOpenCreateProject] = React.useState<boolean>(false)
  const [creatingProject, setCreatingProject] = React.useState<boolean>(false)
  const savedSearchOnlyRedux = useSelector(selectSavedSearch)
  const projects = ProjectRepo.useList()
  const Projects: IProject[] =
    projects.data?.map((x) => {
      return {
        id: x.id ?? '',
        name: x.name,
        createdAt: x.created?.at ?? '',
        description: x.description,
      }
    }) ?? []
  const loading = projects.isLoading
  const error = projects.error
  const projectAddChild = ProjectRepo.useAddChild()
  const [checkboxSelection, setCheckboxSelection] = React.useState<string[]>([])
  const [addedProjects, setAddedProjects] = React.useState<any[]>([])
  const handleClose = () => {
    setShow(false)
    setCheckboxSelection([])
  }

  const handleSaveToProjects = () => {
    if (checkboxSelection.length > 0 && savedSearchOnlyRedux !== null) {
      const projectsToSaveTo: IProject[] | undefined = Projects?.filter((p) =>
        checkboxSelection.includes(p.id)
      )

      //id 326d78cd-a3d7-4176-88c6-21defef103da
      if (projectsToSaveTo && projectsToSaveTo.length > 0) {
        const ptst = projectsToSaveTo.map(async (p) => {
          const search = savedSearchOnlyRedux
          const postData = {
            id: search?.id,
            name: search?.name,
            search_text: search?.search_text,
            algorithm: search?.algorithm,
            keywords: search?.keywords,
            concepts: search?.concepts,
            project_id: p.id,
          }
          if (search?.id) {
            const pid = (await projectAddChild.mutateAsync({
              prop: 'saved_searches',
              parentId: p.id,
              childId: search.id,
              data: postData,
              overrideRoute: `std/project/${p.id}/saved_searches`,
            })) as string
            addedProjects.push(pid)
          }
        })
      }
    }
  }
  React.useEffect(() => {
    if (
      addedProjects &&
      addedProjects.length === checkboxSelection.length &&
      addedProjects.length !== 0
    ) {
      toastr.success(
        `Saved search to ${addedProjects.length} project${addedProjects.length > 1 ? 's' : ''}`
      )
      setAddedProjects([])
      ProjectRepo.invalidateRepo()
      handleClose()
    }
  }, [addedProjects.length])

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 90,
      hideable: true,
      editable: false,
    },
    {
      field: 'name',
      headerName: 'Project name',
      type: 'string',
      width: 400,
      editable: false,
      renderHeader: (params: GridColumnHeaderParams) => <strong>{'Project Name'}</strong>,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'string',
      width: 200,
      editable: false,
      valueGetter: (params: GridValueGetterParams) =>
        `${dayjs(params.row.createdAt).format('ddd - MMM DD, YYYY h:mma') || ''}`,
    },
    {
      field: 'description',
      headerName: 'Description',
      description: 'This column has a value getter and is not sortable.',
      sortable: false,
      width: 200,
      valueGetter: (params: GridValueGetterParams) => `${params.row.description || ''}`,
    },
  ]

  const ProjectTable = React.useMemo(() => {
    return Projects && Projects.length > 0 ? (
      <Box
        sx={{
          height: 300,
          width: '100%',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: indigo[100],
          },
        }}
      >
        <Divider sx={{m: 1}} />

        <Typography variant={'h6'} fontWeight={700}>
          All Projects
        </Typography>

        <DataGrid
          loading={loading}
          rows={Projects}
          density={'compact'}
          columns={columns}
          initialState={{
            columns: {
              columnVisibilityModel: {
                id: false,
                description: true,
                createdAt: true,
              },
            },
          }}
          autoPageSize={true}
          checkboxSelection={true}
          onRowSelectionModelChange={(itm: any) => setCheckboxSelection(itm)}
        />
      </Box>
    ) : (
      <Box
        display={'flex'}
        flexDirection={'column'}
        textAlign={'center'}
        justifyContent={'center'}
        alignItems={'center'}
      >
        <EmptyStateCard title={'No projects created yet'} p={3} m={3} img={'wind'} size={'10%'} />
        <Button
          sx={{width: '20em'}}
          variant={'contained'}
          onClick={() => setOpenCreateProject(true)}
        >
          Create A Project
        </Button>
      </Box>
    )
  }, [projects, Projects, checkboxSelection, savedSearchOnlyRedux])

  return (
    <>
      <Dialog open={show} onClose={() => setShow(false)} maxWidth={'lg'}>
        <DialogTitle id='scroll-dialog-title'>
          <Box color={grey[800]} fontWeight={700}>
            Add Search To Project
          </Box>
          <ShowMoreText
            inheritColor={true}
            variant={'body1'}
            text={discoveryRequest.search_text ?? ''}
            maxLen={300}
            moreString='...Show More'
            lessString='...Show Less'
          />
        </DialogTitle>
        <DialogContent>
          {projectAddChild.isLoading ? (
            <Box
              display={'flex'}
              justifyContent={'center'}
              textAlign={'center'}
              alignItems={'center'}
              minHeight={'20vh'}
            >
              <Box
                display={'flex'}
                flexDirection={'column'}
                alignContent={'center'}
                textAlign={'center'}
                alignItems={'center'}
                gap={10}
              >
                <Typography variant={'h4'} color={grey[600]} mt={5}>
                  Saving to Project...
                </Typography>

                <CircularProgress size={65} />
              </Box>
            </Box>
          ) : (
            <>
              {savedSearchOnlyRedux?.concepts && (
                <Box maxHeight={200} overflow={'scroll'} bgcolor={grey[100]} p={2}>
                  <Typography variant={'h6'} fontWeight={700}>
                    Current Concepts
                  </Typography>
                  <ConceptsList
                    concepts={savedSearchOnlyRedux?.concepts?.filter((f) => f.__lock != 'exclude')}
                    canAdd={false}
                    stackVertical={false}
                  />
                </Box>
              )}
              <Box>{ProjectTable}</Box>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          {checkboxSelection && checkboxSelection.length > 0 && (
            <Button
              variant='contained'
              disabled={projectAddChild.isLoading || creatingProject}
              color={'primary'}
              onClick={handleSaveToProjects}
            >{`Save Search to ${checkboxSelection.length} selected project${
              checkboxSelection.length === 1 ? '' : 's'
            }`}</Button>
          )}
        </DialogActions>
      </Dialog>
      <CreateProjectDialog
        open={openCreateProject}
        setOpen={setOpenCreateProject}
        creating={creatingProject}
        setCreating={setCreatingProject}
      />
    </>
  )
}
